import React, { useMemo, useState } from "react";
import {
  Box,
  Card,
  CardHeader,
  alpha,
  Typography,
  IconButton,
  Stack,
  Collapse,
  Chip,
  TextField,
  InputAdornment,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableSortLabel,
  useTheme,
  Paper,
  Button,
} from "@mui/material";
import { groupByIncomeType, calculateTotalsByIncomeType } from "./calculations";
import { ReadOnlyCell } from "../../utils/CommonTable";
import { SearchableMultiSelectDropdown } from "../../utils/Form/Dropdown";
import { formatAmount } from "../../../utils/hooks/dataHandler";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import RestartAltRoundedIcon from "@mui/icons-material/RestartAltRounded";
import SearchOffOutlinedIcon from "@mui/icons-material/SearchOffOutlined";
import TaxField from "../../utils/TextField/TaxField";
import Summary from "./Summary";

export const buildGroupSubtotal = (rows) => {
  return rows.reduce(
    (totals, row) => {
      return {
        incomeType:
          "Total - " + (row.incomeType === "" ? "Unspecified" : row.incomeType),
        country: "",
        yaEarned: "",
        unremittedIncomeBf:
          totals.unremittedIncomeBf + (row.unremittedIncomeBf || 0),
        currentYearIncome:
          totals.currentYearIncome + (row.currentYearIncome || 0),
        incomeReceived: totals.incomeReceived + (row.incomeReceived || 0),
        incomeUsedNotReceived:
          totals.incomeUsedNotReceived + (row.incomeUsedNotReceived || 0),
        unremittedIncomeCf:
          totals.unremittedIncomeCf + (row.unremittedIncomeCf || 0),
        allowableExpensesBf:
          totals.allowableExpensesBf + (row.allowableExpensesBf || 0),
        currentYearAllowableExpenses:
          totals.currentYearAllowableExpenses +
          (row.currentYearAllowableExpenses || 0),
        shareOfExpensesRemitted:
          totals.shareOfExpensesRemitted + (row.shareOfExpensesRemitted || 0),
        shareOfExpensesNonRemitted:
          totals.shareOfExpensesNonRemitted +
          (row.shareOfExpensesNonRemitted || 0),
        allowableExpensesCf:
          totals.allowableExpensesCf + (row.allowableExpensesCf || 0),
        netIncomeReceived:
          totals.netIncomeReceived + (row.netIncomeReceived || 0),
        foreignTaxSufferedBf:
          totals.foreignTaxSufferedBf + (row.foreignTaxSufferedBf || 0),
        foreignTaxSuffered:
          totals.foreignTaxSuffered + (row.foreignTaxSuffered || 0),
        foreignTaxSufferedCf:
          totals.foreignTaxSufferedCf + (row.foreignTaxSufferedCf || 0),
        shareOfForeignTaxSufferedRemitted:
          totals.shareOfForeignTaxSufferedRemitted +
          (row.shareOfForeignTaxSufferedRemitted || 0),
        shareOfForeignTaxSufferedNonRemitted:
          totals.shareOfForeignTaxSufferedNonRemitted +
          (row.shareOfForeignTaxSufferedNonRemitted || 0),
      };
    },
    {
      incomeType: "Total",
      country: "",
      yaEarned: "",
      unremittedIncomeBf: 0,
      currentYearIncome: 0,
      incomeReceived: 0,
      incomeUsedNotReceived: 0,
      unremittedIncomeCf: 0,
      allowableExpensesBf: 0,
      currentYearAllowableExpenses: 0,
      shareOfExpensesRemitted: 0,
      shareOfExpensesNonRemitted: 0,
      allowableExpensesCf: 0,
      netIncomeReceived: 0,
      foreignTaxSufferedBf: 0,
      foreignTaxSuffered: 0,
      foreignTaxSufferedCf: 0,
      shareOfForeignTaxSufferedRemitted: 0,
      shareOfForeignTaxSufferedNonRemitted: 0,
    }
  );
};

export function buildGroupedRows({
  data,
  groupByFn,
  subtotalFn,
  totalsByTypeFn,
  groupHeaderKey = "incomeType",
  subtotalOverrides = (subtotal, calcTotals) => {
    // Default: override nothing
    return subtotal;
  },
}) {
  if (!data || data.length === 0) return [];

  const groups = groupByFn(data);
  const totalsByType = totalsByTypeFn ? totalsByTypeFn(data) : {};

  const output = [];
  Object.keys(groups).forEach((groupKey) => {
    const rows = groups[groupKey];
    // Group header
    output.push({ __group: true, [groupHeaderKey]: groupKey });
    // Group rows
    rows.forEach((r) => output.push(r));
    // Group subtotal
    let subtotal = subtotalFn(rows);
    const calcTotals = totalsByType[groupKey];
    if (calcTotals) {
      subtotal = subtotalOverrides(subtotal, calcTotals);
    }
    output.push({ __subtotal: true, [groupHeaderKey]: groupKey, ...subtotal });
  });

  return output;
}

const getTrackingColumns = (
  onChange,
  canEdit = true,
  onSortYaEarned,
  sortDirection
) => [
  {
    key: "country",
    label: "Country",
    group: "meta",
    headerAlign: "center",
    colAlign: "center",
    sticky: true,
    stickyLeft: 0,
    width: 160,
    render: (row) => (
      <Box sx={{ minWidth: 120 }}>
        <ReadOnlyCell value={row.country || "Unspecified"} width='100%' />
      </Box>
    ),
  },
  {
    key: "yaEarned",
    label: onSortYaEarned ? (
      <TableSortLabel
        active={Boolean(sortDirection)}
        direction={sortDirection || "asc"}
        onClick={() =>
          onSortYaEarned(sortDirection === "asc" ? "desc" : "asc")
        }
        aria-label='Sort tracking rows by YA earned'
      >
        YA Earned
      </TableSortLabel>
    ) : (
      "YA Earned"
    ),
    group: "meta",
    headerAlign: "center",
    colAlign: "center",
    sticky: true,
    stickyLeft: 160,
    render: (row) => <ReadOnlyCell value={row.yaEarned} width='100%' />,
  },
  {
    key: "currentYearIncome",
    label: (
      <>
        Unremitted b/f /<br />
        Current YA Income (A)
      </>
    ),
    group: "income",
    headerAlign: "right",
    colAlign: "right",
    render: (row) => (
      <ReadOnlyCell
        value={formatAmount(
          (row.unremittedIncomeBf || 0) + (row.currentYearIncome || 0)
        )}
        width='100%'
        textAlign='right'
      />
    ),
  },
  {
    key: "incomeReceived",
    label: "Received in SG (B)",
    group: "income",
    headerAlign: "right",
    colAlign: "right",
    render: (row) => (
      <TaxField
        value={row.incomeReceived}
        onChange={onChange}
        reference={{
          rowId: row.rowId,
          field: "incomeReceived",
          fieldType: "numeric",
        }}
        canEdit={canEdit}
      />
    ),
  },
  {
    key: "incomeUsedNotReceived",
    label: "Used not Received (C)",
    group: "income",
    headerAlign: "right",
    colAlign: "right",
    render: (row) => (
      <TaxField
        value={row.incomeUsedNotReceived}
        onChange={onChange}
        reference={{
          rowId: row.rowId,
          field: "incomeUsedNotReceived",
          fieldType: "numeric",
        }}
        canEdit={canEdit}
      />
    ),
  },
  {
    key: "unremittedIncomeCf",
    label: "Unremitted c/f (D)",
    group: "income",
    headerAlign: "right",
    colAlign: "right",
    render: (row) => (
      <ReadOnlyCell
        value={formatAmount(row.unremittedIncomeCf)}
        width='100%'
        textAlign='right'
      />
    ),
  },
  {
    key: "currentYearAllowableExpenses",
    label: (
      <>
        Allowable Expenses b/f /<br />
        Current YA Allowable
        <br /> Expenses (E)
      </>
    ),
    group: "expenses",
    headerAlign: "right",
    colAlign: "right",
    render: (row) => (
      <ReadOnlyCell
        value={formatAmount(
          (row.allowableExpensesBf || 0) +
            (row.currentYearAllowableExpenses || 0)
        )}
        width='100%'
        textAlign='right'
      />
    ),
  },
  {
    key: "shareOfExpensesRemitted",
    label: (
      <>
        Share of Expenses
        <br />
        Remitted (F)
      </>
    ),
    group: "expenses",
    headerAlign: "right",
    colAlign: "right",
    render: (row) => (
      <ReadOnlyCell
        value={formatAmount(row.shareOfExpensesRemitted)}
        width='100%'
        textAlign='right'
      />
    ),
  },
  {
    key: "shareOfExpensesNonRemitted",
    label: (
      <>
        Share of Expenses for
        <br />
        Non-Remitted Income (G)
      </>
    ),
    group: "expenses",
    headerAlign: "right",
    colAlign: "right",
    render: (row) => (
      <ReadOnlyCell
        value={formatAmount(row.shareOfExpensesNonRemitted)}
        width='100%'
        textAlign='right'
      />
    ),
  },
  {
    key: "allowableExpensesCf",
    label: (
      <>
        Allowable <br />
        Expenses c/f (H)
      </>
    ),
    group: "expenses",
    headerAlign: "right",
    colAlign: "right",
    render: (row) => (
      <ReadOnlyCell
        value={formatAmount(row.allowableExpensesCf)}
        width='100%'
        textAlign='right'
      />
    ),
  },
  {
    key: "netIncomeReceived",
    label: "Net Income (I)",
    group: "foreign",
    headerAlign: "right",
    colAlign: "right",
    render: (row) => (
      <ReadOnlyCell
        value={formatAmount(row.netIncomeReceived)}
        width='100%'
        textAlign='right'
      />
    ),
  },
  {
    key: "foreignTaxSuffered",
    label: (
      <>
        Foreign Tax b/f /<br />
        Current YA Foreign Tax (J)
      </>
    ),
    group: "foreign",
    headerAlign: "right",
    colAlign: "right",
    render: (row) => (
      <ReadOnlyCell
        value={formatAmount(
          (row.foreignTaxSufferedBf || 0) + (row.foreignTaxSuffered || 0),
          false
        )}
        width='100%'
        textAlign='right'
      />
    ),
  },
  {
    key: "shareOfForeignTaxSufferedRemitted",
    label: (
      <>
        Share of Tax
        <br />
        Remitted (K)
      </>
    ),
    group: "foreign",
    headerAlign: "right",
    colAlign: "right",
    render: (row) => (
      <ReadOnlyCell
        value={formatAmount(row.shareOfForeignTaxSufferedRemitted || 0, false)}
        width='100%'
        textAlign='right'
      />
    ),
  },
  {
    key: "shareOfForeignTaxSufferedNonRemitted",
    label: (
      <>
        Share of Tax for
        <br />
        Non-Remitted Income (L)
      </>
    ),
    group: "foreign",
    headerAlign: "right",
    colAlign: "right",
    render: (row) => (
      <ReadOnlyCell
        value={formatAmount(
          row.shareOfForeignTaxSufferedNonRemitted || 0,
          false
        )}
        width='100%'
        textAlign='right'
      />
    ),
  },
  {
    key: "foreignTaxSufferedCf",
    label: "Foreign Tax c/f (M)",
    group: "foreign",
    headerAlign: "right",
    colAlign: "right",
    render: (row) => (
      <ReadOnlyCell
        value={formatAmount(row.foreignTaxSufferedCf || 0, false)}
        width='100%'
        textAlign='right'
      />
    ),
  },
];

const GroupedTrackingTable = ({ rows, columns, noRowsHint }) => {
  const theme = useTheme();
  const incomeColumns = columns.filter((c) => c.group === "income");
  const expenseColumns = columns.filter((c) => c.group === "expenses");
  const foreignColumns = columns.filter((c) => c.group === "foreign");
  const metaColumns = columns.filter((c) => c.group === "meta");

  const groupBg = (group) => {
    switch (group) {
      case "income":
        return alpha(theme.palette.success.main, 0.04);
      case "expenses":
        return alpha(theme.palette.error.main, 0.04);
      case "foreign":
        return alpha(theme.palette.info.main, 0.04);
      case "meta":
      default:
        return theme.palette.background.paper;
    }
  };

  if (!rows || rows.length === 0) {
    return (
      <Box sx={{ py: 4, textAlign: "center", color: "text.secondary" }}>
        <Typography variant='body2'>{noRowsHint}</Typography>
      </Box>
    );
  }

  // Compute left offsets for sticky columns based on declared widths
  const leftOffsets = {};
  let offset = 0;
  columns.forEach((col) => {
    if (col.sticky) {
      leftOffsets[col.key] = offset;
      const baseWidth = col.width - 20 || 160;
      offset += baseWidth;
    }
  });

  return (
    <TableContainer
      component={Paper}
      elevation={0}
      sx={{ backgroundColor: "background.paper" }}
    >
      <Table stickyHeader size='small'>
        <TableHead>
          {/* Group Header Row */}
          <TableRow>
            {columns.map((col, index) => {
              let groupLabel = "";
              let colSpan = 1;

              if (col.group === "meta" && index === 0) {
                colSpan = metaColumns.length;
              } else if (
                col.group === "income" &&
                incomeColumns[0]?.key === col.key
              ) {
                groupLabel = "Income Block";
                colSpan = incomeColumns.length;
              } else if (
                col.group === "expenses" &&
                expenseColumns[0]?.key === col.key
              ) {
                groupLabel = "Expenses Block";
                colSpan = expenseColumns.length;
              } else if (
                col.group === "foreign" &&
                foreignColumns[0]?.key === col.key
              ) {
                groupLabel = "Foreign Tax Suffered Block";
                colSpan = foreignColumns.length;
              } else {
                return null;
              }

              const stickyStyles = col.sticky
                ? {
                    position: "sticky",
                    left: leftOffsets[col.key],
                    zIndex: 3,
                  }
                : {};

              return (
                <TableCell
                  key={`group-${col.key}`}
                  align='center'
                  colSpan={colSpan}
                  sx={{
                    fontWeight: 600,
                    fontSize: "0.75rem",
                    textTransform: "uppercase",
                    letterSpacing: "0.06em",
                    borderBottom: "1px solid",
                    borderColor: alpha(theme.palette.divider, 0.4),
                    backgroundColor: groupBg(col.group),
                    ...stickyStyles,
                  }}
                >
                  {groupLabel}
                </TableCell>
              );
            })}
          </TableRow>

          {/* Column Header Row */}
          <TableRow>
            {columns.map((col) => {
              const stickyStyles = col.sticky
                ? {
                    position: "sticky",
                    left: leftOffsets[col.key],
                    zIndex: 4,
                  }
                : {};

              return (
                <TableCell
                  key={col.key}
                  align={col.headerAlign || "center"}
                  sx={{
                    fontWeight: 600,
                    fontSize: "0.75rem",
                    textTransform: "uppercase",
                    letterSpacing: "0.06em",
                    whiteSpace: "nowrap",
                    borderBottom: "1px solid",
                    borderColor: alpha(theme.palette.divider, 0.4),
                    width: col.width || "auto",
                    backgroundColor: groupBg(col.group),
                    ...stickyStyles,
                  }}
                >
                  {col.label}
                </TableCell>
              );
            })}
          </TableRow>
        </TableHead>

        {/* Table Body */}
        <TableBody>
          {rows.map((row, rowIndex) => (
            <TableRow
              key={row.rowId || rowIndex}
              sx={{
                "&:hover": {
                  bgcolor: alpha(theme.palette.primary.main, 0.03),
                },
                transition: "background-color 0.2s ease-in-out",
              }}
            >
              {columns.map((col) => {
                const stickyStyles = col.sticky
                  ? {
                      position: "sticky",
                      left: leftOffsets[col.key],
                      zIndex: 2,
                      backgroundColor: groupBg(col.group),
                    }
                  : {};

                return (
                  <TableCell
                    key={`${col.key}-${rowIndex}`}
                    align={col.colAlign || "center"}
                    sx={{
                      fontSize: "0.8rem",
                      borderBottom: "1px solid",
                      borderColor: alpha(theme.palette.divider, 0.2),
                      p: 1.25,
                      width: col.width || "auto",
                      backgroundColor: groupBg(col.group),
                      ...stickyStyles,
                    }}
                  >
                    {col.render ? col.render(row, rowIndex) : row[col.key]}
                  </TableCell>
                );
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const TrackerForm = ({
  data,
  onChange,
  calculatedTrackingData,
  ftcClaims,
  onFtcClaimChange,
  onSortYaEarned,
  sortDirection,
  canEdit = true,
}) => {
  const [selectedCountries, setSelectedCountries] = useState([]);
  const [selectedYears, setSelectedYears] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [expandedIncomeTypes, setExpandedIncomeTypes] = useState({});

  const availableCountries = useMemo(() => {
    const set = new Set();
    (data || []).forEach((row) => {
      if (row.country) set.add(row.country);
    });
    return Array.from(set);
  }, [data]);

  const availableYears = useMemo(() => {
    const set = new Set();
    (data || []).forEach((row) => {
      if (row.yaEarned) set.add(String(row.yaEarned));
    });
    return Array.from(set);
  }, [data]);

  const countryFilterOptions = useMemo(
    () => availableCountries.map((country) => ({ value: country, label: country })),
    [availableCountries]
  );
  const yearFilterOptions = useMemo(
    () => availableYears.map((year) => ({ value: year, label: year })),
    [availableYears]
  );

  const filteredData = useMemo(
    () =>
      (data || []).filter((row) => {
        const matchesCountry =
          selectedCountries.length === 0 ||
          selectedCountries.includes(row.country);
        const matchesYear =
          selectedYears.length === 0 ||
          selectedYears.includes(String(row.yaEarned));
        const query = searchText.trim().toLowerCase();
        const matchesSearch =
          !query ||
          [row.incomeType, row.country, row.yaEarned, row.description]
            .filter(Boolean)
            .some((v) => String(v).toLowerCase().includes(query));

        return matchesCountry && matchesYear && matchesSearch;
      }),
    [data, selectedCountries, selectedYears, searchText]
  );

  const groupedByIncomeType = useMemo(
    () => groupByIncomeType(filteredData || []),
    [filteredData]
  );

  const totalsByIncomeType = useMemo(
    () => calculateTotalsByIncomeType(filteredData || []),
    [filteredData]
  );

  const incomeTypeKeys = Object.keys(groupedByIncomeType);
  const hasActiveFilters =
    selectedCountries.length > 0 ||
    selectedYears.length > 0 ||
    Boolean(searchText.trim());
  const clearFilters = () => {
    setSelectedCountries([]);
    setSelectedYears([]);
    setSearchText("");
  };
  const filterFieldSx = {
    minWidth: 0,
    "& .MuiOutlinedInput-root": {
      borderRadius: 1.5,
      bgcolor: "background.paper",
    },
  };
  const filterChipSx = {
    maxWidth: "100%",
    borderRadius: 1,
    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.06),
    borderColor: (theme) => alpha(theme.palette.primary.main, 0.2),
    "& .MuiChip-deleteIcon": {
      color: (theme) => alpha(theme.palette.primary.main, 0.65),
      "&:hover": { color: "primary.main" },
    },
  };

  if (!data || data.length === 0) {
    return (
      <Box
        sx={{
          p: 4,
          textAlign: "center",
          color: "text.secondary",
        }}
      >
        <Typography variant='body1' gutterBottom>
          No tracking schedule data yet.
        </Typography>
        <Typography variant='body2'>
          Lock rows from the Current Year section above to populate this view.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%", p: { xs: 2, sm: 3 } }}>
      <Stack spacing={3}>
        {/* Filters */}
        <Box
          component='section'
          aria-label='Filter tracking rows'
          sx={{
            borderRadius: 2,
            bgcolor: "background.paper",
            border: "1px solid",
            borderColor: (theme) => alpha(theme.palette.primary.main, 0.16),
            overflow: "hidden",
          }}
        >
          <Stack
            direction='row'
            alignItems='center'
            justifyContent='space-between'
            flexWrap='wrap'
            gap={1.5}
            sx={{
              px: 2,
              py: 1.5,
              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.03),
              borderBottom: "1px solid",
              borderColor: (theme) => alpha(theme.palette.divider, 0.6),
            }}
          >
            <Stack direction='row' spacing={1.25} alignItems='center'>
              <Box
                sx={{
                  display: "grid",
                  placeItems: "center",
                  width: 34,
                  height: 34,
                  flexShrink: 0,
                  borderRadius: 1.5,
                  bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
                  color: "primary.main",
                }}
              >
                <FilterAltOutlinedIcon sx={{ fontSize: 20 }} />
              </Box>
              <Box>
                <Typography variant='subtitle2' fontWeight={700}>
                  Filter tracking rows
                </Typography>
                <Typography
                  variant='caption'
                  color='text.secondary'
                  role='status'
                  aria-live='polite'
                >
                  Showing {filteredData.length} of {data.length} rows
                </Typography>
              </Box>
            </Stack>
            <Button
              size='small'
              startIcon={<RestartAltRoundedIcon />}
              disabled={!hasActiveFilters}
              onClick={clearFilters}
              sx={{ textTransform: "none", borderRadius: 1.5, ml: "auto" }}
            >
              Clear filters
            </Button>
          </Stack>
          <Box sx={{ p: 2 }}>
            <Box
              sx={{
                display: "grid",
                gap: 1.5,
                gridTemplateColumns: {
                  xs: "minmax(0, 1fr)",
                  sm: "repeat(2, minmax(0, 1fr))",
                  lg: "minmax(0, 1fr) minmax(0, 0.8fr) minmax(0, 1.6fr)",
                },
              }}
            >
              <SearchableMultiSelectDropdown
                label='Country'
                value={selectedCountries}
                options={countryFilterOptions}
                onChange={setSelectedCountries}
                placeholder='All countries'
                getSelectionSummary={(selected) =>
                  selected.length === 1
                    ? selected[0].label
                    : `${selected.length} countries selected`
                }
                sx={{ minWidth: 0 }}
              />
              <SearchableMultiSelectDropdown
                label='YA Earned'
                value={selectedYears}
                options={yearFilterOptions}
                onChange={setSelectedYears}
                placeholder='All years'
                getSelectionSummary={(selected) =>
                  selected.length === 1
                    ? selected[0].label
                    : `${selected.length} years selected`
                }
                sx={{ minWidth: 0 }}
              />
              <TextField
                label='Search'
                placeholder='Income type, country or YA'
                size='small'
                fullWidth
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position='start'>
                      <SearchOutlinedIcon
                        sx={{ fontSize: 20, color: "text.secondary" }}
                      />
                    </InputAdornment>
                  ),
                  endAdornment: searchText ? (
                    <InputAdornment position='end'>
                      <IconButton
                        size='small'
                        aria-label='Clear search'
                        onClick={() => setSearchText("")}
                      >
                        <CloseRoundedIcon fontSize='small' />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                }}
                sx={{
                  ...filterFieldSx,
                  gridColumn: { sm: "1 / -1", lg: "auto" },
                }}
              />
            </Box>

            {/* Active filter chips */}
            {hasActiveFilters && (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1.5 }}>
                {selectedCountries.map((country) => (
                  <Chip
                    key={`country-${country || "unspecified"}`}
                    label={`Country: ${country || "Unspecified"}`}
                    size='small'
                    variant='outlined'
                    color='primary'
                    onDelete={() =>
                      setSelectedCountries((prev) =>
                        prev.filter((c) => c !== country)
                      )
                    }
                    sx={filterChipSx}
                  />
                ))}
                {selectedYears.map((year) => (
                  <Chip
                    key={`year-${year}`}
                    label={`YA: ${year}`}
                    size='small'
                    variant='outlined'
                    color='primary'
                    onDelete={() =>
                      setSelectedYears((prev) => prev.filter((y) => y !== year))
                    }
                    sx={filterChipSx}
                  />
                ))}
                {searchText.trim() && (
                  <Chip
                    key='search'
                    label={`Search: "${searchText.trim()}"`}
                    size='small'
                    variant='outlined'
                    color='primary'
                    onDelete={() => setSearchText("")}
                    sx={filterChipSx}
                  />
                )}
              </Box>
            )}
            {filteredData.length === 0 && (
              <Stack
                direction='row'
                spacing={1}
                alignItems='center'
                sx={{ mt: 2, color: "text.secondary" }}
              >
                <SearchOffOutlinedIcon sx={{ fontSize: 20 }} />
                <Typography variant='body2'>
                  No matching rows. Remove a filter or try another search.
                </Typography>
              </Stack>
            )}
          </Box>
        </Box>

        {incomeTypeKeys.map((incomeTypeKey) => {
          const rows = groupedByIncomeType[incomeTypeKey] || [];
          const totals = totalsByIncomeType[incomeTypeKey];
          const displayIncomeType =
            incomeTypeKey === "" || incomeTypeKey == null
              ? "Unspecified income type"
              : incomeTypeKey;
          const isExpanded =
            expandedIncomeTypes[incomeTypeKey] !== undefined
              ? expandedIncomeTypes[incomeTypeKey]
              : true;

          return (
            <Card
              key={incomeTypeKey || "unspecified"}
              variant='outlined'
              sx={{
                borderRadius: 2,
                borderColor: "divider",
                boxShadow: "none",
              }}
            >
              <CardHeader
                title={
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      flexWrap: "wrap",
                    }}
                  >
                    <Typography variant='subtitle1' fontWeight={700}>
                      {displayIncomeType}
                    </Typography>
                    <Chip
                      label={`${rows.length} row${
                        rows.length !== 1 ? "s" : ""
                      }`}
                      size='small'
                      color='primary'
                      variant='outlined'
                    />
                  </Box>
                }
                subheader={
                  totals ? (
                    <Box sx={{ mt: 0.5 }}>
                      <Typography variant='caption' color='text.secondary'>
                        Net income for this income type:{" "}
                        <strong>
                          {formatAmount(totals.totalNetIncome || 0)}
                        </strong>
                      </Typography>
                      <Typography
                        variant='caption'
                        color='text.secondary'
                        sx={{ display: "block" }}
                      >
                        Share of foreign tax suffered (remitted):{" "}
                        <strong>
                          {formatAmount(totals.totalForeignTax || 0, false)}
                        </strong>
                      </Typography>
                    </Box>
                  ) : null
                }
                action={
                  <IconButton
                    size='small'
                    onClick={() =>
                      setExpandedIncomeTypes((prev) => ({
                        ...prev,
                        [incomeTypeKey]: !isExpanded,
                      }))
                    }
                  >
                    <ExpandMoreIcon
                      fontSize='small'
                      sx={{
                        transform: isExpanded
                          ? "rotate(180deg)"
                          : "rotate(0deg)",
                        transition: "transform 0.2s ease-in-out",
                      }}
                    />
                  </IconButton>
                }
              />
              <Collapse in={isExpanded} timeout='auto' unmountOnExit>
                <GroupedTrackingTable
                  rows={rows}
                  columns={getTrackingColumns(
                    onChange,
                    canEdit,
                    onSortYaEarned,
                    sortDirection
                  )}
                  noRowsHint='No tracking rows for this income type.'
                />
              </Collapse>
            </Card>
          );
        })}
      </Stack>

      <Summary
        data={calculatedTrackingData}
        ftcClaims={ftcClaims}
        onFtcClaimChange={onFtcClaimChange}
        canEdit={canEdit}
      />
    </Box>
  );
};

export default TrackerForm;
